/**
 * What a `CustomTransactions` row IS — the single source for every label, colour and
 * direction the dashboard derives from one.
 *
 * Mirrors data-service's `customTransactionEffect`. Both exist because the type name is not
 * safely parseable: `transactionType.startsWith('REMOVE')` read a `CORRECTION_STABLE_BALANCE`
 * as a deposit and labelled a chart dot "BALANCE ADDED" on a balance that had just stepped
 * DOWN, and `transactionType.includes('_BALANCE')` misses `DISTRIBUTION` and `EARN_INTEREST`
 * entirely, which sends them through the core-transaction branch and renders a profit they
 * never realised.
 *
 * `null` means "not a custom transaction" — i.e. a real order.
 */
export type CustomTransactionKind =
  /** A CPC swap rebalancing a subscription between its own two sides. */
  | 'DISTRIBUTION'
  /** The user put money in. */
  | 'DEPOSIT'
  /** The user took money out. */
  | 'WITHDRAWAL'
  /** A recorded balance brought back to what the exchange holds — the value was never there. */
  | 'CORRECTION'
  /** Earn yield credited to the subscription. */
  | 'INTEREST';

export const customTransactionKind = (
  transactionType: string | undefined,
): CustomTransactionKind | null => {
  switch (transactionType) {
    case 'DISTRIBUTION':
      return 'DISTRIBUTION';

    case 'ADD_STABLE_BALANCE':
    case 'ADD_ASSET_BALANCE':
      return 'DEPOSIT';

    case 'REMOVE_STABLE_BALANCE':
    case 'REMOVE_ASSET_BALANCE':
      return 'WITHDRAWAL';

    case 'CORRECTION_STABLE_BALANCE':
    case 'CORRECTION_ASSET_BALANCE':
      return 'CORRECTION';

    case 'EARN_INTEREST':
      return 'INTEREST';

    default:
      return null;
  }
};

export const isCustomTransactionType = (
  transactionType: string | undefined,
): boolean => customTransactionKind(transactionType) !== null;

/**
 * Header shown on a chart-dot tooltip. A `DISTRIBUTION` takes the direction of the trade it
 * made; the rest are not trades and carry none.
 */
export const customTransactionTitle = (
  transactionType: string | undefined,
  orderType?: string,
): string | null => {
  switch (customTransactionKind(transactionType)) {
    case 'DISTRIBUTION':
      return `DISTRIBUTION ${orderType ?? ''}`.trim();
    case 'DEPOSIT':
      return 'BALANCE ADDED';
    case 'WITHDRAWAL':
      return 'BALANCE REMOVED';
    case 'CORRECTION':
      return 'BALANCE CORRECTED';
    case 'INTEREST':
      return 'EARN INTEREST';
    default:
      return null;
  }
};

/**
 * Which way value moved, for a row that is not a trade. A correction only ever takes value
 * away — it exists to write off a balance that was recorded but not held.
 */
export const customTransactionFlow = (
  transactionType: string | undefined,
): 'IN' | 'OUT' | null => {
  switch (customTransactionKind(transactionType)) {
    case 'DEPOSIT':
    case 'INTEREST':
      return 'IN';
    case 'WITHDRAWAL':
    case 'CORRECTION':
      return 'OUT';
    default:
      return null;
  }
};
